import { outlineOf } from './outline';
import { zip } from './zip';
export const PPTX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
/** Bullets per slide before a branch continues on the next one. */
export const BULLETS_PER_SLIDE = 12;
// A 16:9 slide, in EMU (914,400 to the inch).
const SLIDE_WIDTH = 12_192_000;
const SLIDE_HEIGHT = 6_858_000;
const MARGIN = 609_600;
const INK = '1F1B2E';
const MUTED = '6B6880';
const ACCENT = '6D5AE6';
function xml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}
const NS = 'xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" ' +
    'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" ' +
    'xmlns:p="http://schemas.openxmlformats.org/presentationml/2006/main"';
const DECLARATION = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
const REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const EMPTY_TREE = '<p:nvGrpSpPr><p:cNvPr id="1" name=""/><p:cNvGrpSpPr/><p:nvPr/></p:nvGrpSpPr>' +
    '<p:grpSpPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/>' +
    '<a:chOff x="0" y="0"/><a:chExt cx="0" cy="0"/></a:xfrm></p:grpSpPr>';
function textBox(id, name, box, anchor, body) {
    return (`<p:sp><p:nvSpPr><p:cNvPr id="${String(id)}" name="${xml(name)}"/><p:cNvSpPr txBox="1"/><p:nvPr/></p:nvSpPr>` +
        `<p:spPr><a:xfrm><a:off x="${String(box.x)}" y="${String(box.y)}"/>` +
        `<a:ext cx="${String(box.width)}" cy="${String(box.height)}"/></a:xfrm>` +
        '<a:prstGeom prst="rect"><a:avLst/></a:prstGeom><a:noFill/></p:spPr>' +
        `<p:txBody><a:bodyPr wrap="square" lIns="0" rIns="0" anchor="${anchor}"><a:normAutofit/></a:bodyPr>` +
        `<a:lstStyle/>${body}</p:txBody></p:sp>`);
}
function run(text, size, color, bold = false) {
    return (`<a:r><a:rPr lang="en-US" sz="${String(size)}"${bold ? ' b="1"' : ''} dirty="0">` +
        `<a:solidFill><a:srgbClr val="${color}"/></a:solidFill><a:latin typeface="Calibri"/></a:rPr>` +
        `<a:t>${xml(text)}</a:t></a:r>`);
}
function accentBar(id) {
    return (`<p:sp><p:nvSpPr><p:cNvPr id="${String(id)}" name="Accent"/><p:cNvSpPr/><p:nvPr/></p:nvSpPr>` +
        `<p:spPr><a:xfrm><a:off x="${String(MARGIN)}" y="${String(MARGIN)}"/>` +
        `<a:ext cx="91440" cy="${String(914_400)}"/></a:xfrm>` +
        `<a:prstGeom prst="rect"><a:avLst/></a:prstGeom><a:solidFill><a:srgbClr val="${ACCENT}"/></a:solidFill>` +
        '<a:ln><a:noFill/></a:ln></p:spPr></p:sp>');
}
function slide(shapes) {
    return (`${DECLARATION}<p:sld ${NS}><p:cSld><p:bg><p:bgPr><a:solidFill><a:srgbClr val="FFFFFF"/></a:solidFill>` +
        `<a:effectLst/></p:bgPr></p:bg><p:spTree>${EMPTY_TREE}${shapes}</p:spTree></p:cSld>` +
        '<p:clrMapOvr><a:masterClrMapping/></p:clrMapOvr></p:sld>');
}
function openingSlide(title, subtitle, picture) {
    if (picture === undefined) {
        const titleBox = {
            x: MARGIN,
            y: 2_057_400,
            width: SLIDE_WIDTH - 2 * MARGIN,
            height: 1_600_200,
        };
        const subtitleBox = {
            x: MARGIN,
            y: 3_733_800,
            width: SLIDE_WIDTH - 2 * MARGIN,
            height: 685_800,
        };
        return slide(textBox(2, 'Title', titleBox, 'b', `<a:p>${run(title, 4400, INK, true)}</a:p>`) +
            textBox(3, 'Subtitle', subtitleBox, 't', `<a:p>${run(subtitle, 2000, MUTED)}</a:p>`));
    }
    // Title across the top, the whole map fitted into what is left, keeping its proportions.
    const titleBox = { x: MARGIN, y: 381_000, width: SLIDE_WIDTH - 2 * MARGIN, height: 990_600 };
    const area = {
        x: MARGIN,
        y: 1_524_000,
        width: SLIDE_WIDTH - 2 * MARGIN,
        height: SLIDE_HEIGHT - 1_524_000 - MARGIN,
    };
    const scale = Math.min(area.width / picture.width, area.height / picture.height);
    const width = Math.round(picture.width * scale);
    const height = Math.round(picture.height * scale);
    const x = Math.round(area.x + (area.width - width) / 2);
    const y = Math.round(area.y + (area.height - height) / 2);
    return slide(textBox(2, 'Title', titleBox, 'ctr', `<a:p>${run(title, 3600, INK, true)}</a:p>`) +
        '<p:pic><p:nvPicPr><p:cNvPr id="3" name="Map"/><p:cNvPicPr><a:picLocks noChangeAspect="1"/></p:cNvPicPr>' +
        '<p:nvPr/></p:nvPicPr><p:blipFill><a:blip r:embed="rId2"/><a:stretch><a:fillRect/></a:stretch></p:blipFill>' +
        `<p:spPr><a:xfrm><a:off x="${String(x)}" y="${String(y)}"/><a:ext cx="${String(width)}" cy="${String(height)}"/>` +
        '</a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></p:spPr></p:pic>');
}
function branchSlide(title, bullets, branchDepth) {
    const titleBox = {
        x: MARGIN + 274_320,
        y: MARGIN,
        width: SLIDE_WIDTH - 2 * MARGIN - 274_320,
        height: 914_400,
    };
    const bodyBox = {
        x: MARGIN + 274_320,
        y: 1_828_800,
        width: SLIDE_WIDTH - 2 * MARGIN - 274_320,
        height: SLIDE_HEIGHT - 1_828_800 - MARGIN,
    };
    const paragraphs = bullets
        .map((item) => {
        // Four levels is what a slide can indent legibly; anything deeper sits at the fourth.
        const level = Math.min(item.depth - branchDepth - 1, 4);
        const margin = 342_900 + level * 457_200;
        const size = Math.max(1400, 2200 - level * 200);
        return (`<a:p><a:pPr marL="${String(margin)}" lvl="${String(level)}" indent="-285750">` +
            `<a:buClr><a:srgbClr val="${ACCENT}"/></a:buClr><a:buFont typeface="Arial"/><a:buChar char="•"/></a:pPr>` +
            `${run(item.text, size, level === 0 ? INK : MUTED)}</a:p>`);
    })
        .join('');
    return slide(accentBar(4) +
        textBox(2, 'Title', titleBox, 'ctr', `<a:p>${run(title, 3200, INK, true)}</a:p>`) +
        (paragraphs === '' ? '' : textBox(3, 'Points', bodyBox, 't', paragraphs)));
}
const THEME = `${DECLARATION}<a:theme xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" name="Orbit Mind">` +
    '<a:themeElements><a:clrScheme name="Orbit Mind">' +
    '<a:dk1><a:srgbClr val="1F1B2E"/></a:dk1><a:lt1><a:srgbClr val="FFFFFF"/></a:lt1>' +
    '<a:dk2><a:srgbClr val="2E2A45"/></a:dk2><a:lt2><a:srgbClr val="F4F3FA"/></a:lt2>' +
    '<a:accent1><a:srgbClr val="6D5AE6"/></a:accent1><a:accent2><a:srgbClr val="22B8CF"/></a:accent2>' +
    '<a:accent3><a:srgbClr val="F59F00"/></a:accent3><a:accent4><a:srgbClr val="E64980"/></a:accent4>' +
    '<a:accent5><a:srgbClr val="40C057"/></a:accent5><a:accent6><a:srgbClr val="4C6EF5"/></a:accent6>' +
    '<a:hlink><a:srgbClr val="4C6EF5"/></a:hlink><a:folHlink><a:srgbClr val="7048E8"/></a:folHlink>' +
    '</a:clrScheme><a:fontScheme name="Orbit Mind">' +
    '<a:majorFont><a:latin typeface="Calibri"/><a:ea typeface=""/><a:cs typeface=""/></a:majorFont>' +
    '<a:minorFont><a:latin typeface="Calibri"/><a:ea typeface=""/><a:cs typeface=""/></a:minorFont>' +
    '</a:fontScheme><a:fmtScheme name="Orbit Mind"><a:fillStyleLst>' +
    '<a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:solidFill><a:schemeClr val="phClr"/></a:solidFill>' +
    '<a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:fillStyleLst><a:lnStyleLst>' +
    '<a:ln w="6350"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:ln>' +
    '<a:ln w="12700"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:ln>' +
    '<a:ln w="19050"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:ln></a:lnStyleLst>' +
    '<a:effectStyleLst><a:effectStyle><a:effectLst/></a:effectStyle><a:effectStyle><a:effectLst/></a:effectStyle>' +
    '<a:effectStyle><a:effectLst/></a:effectStyle></a:effectStyleLst><a:bgFillStyleLst>' +
    '<a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:solidFill><a:schemeClr val="phClr"/></a:solidFill>' +
    '<a:solidFill><a:schemeClr val="phClr"/></a:solidFill></a:bgFillStyleLst></a:fmtScheme></a:themeElements>' +
    '</a:theme>';
const MASTER = `${DECLARATION}<p:sldMaster ${NS}><p:cSld><p:spTree>${EMPTY_TREE}</p:spTree></p:cSld>` +
    '<p:clrMap bg1="lt1" tx1="dk1" bg2="lt2" tx2="dk2" accent1="accent1" accent2="accent2" accent3="accent3" ' +
    'accent4="accent4" accent5="accent5" accent6="accent6" hlink="hlink" folHlink="folHlink"/>' +
    '<p:sldLayoutIdLst><p:sldLayoutId id="2147483649" r:id="rId1"/></p:sldLayoutIdLst></p:sldMaster>';
const LAYOUT = `${DECLARATION}<p:sldLayout ${NS} type="blank" preserve="1"><p:cSld name="Blank"><p:spTree>${EMPTY_TREE}` +
    '</p:spTree></p:cSld><p:clrMapOvr><a:masterClrMapping/></p:clrMapOvr></p:sldLayout>';
function relationships(items) {
    const rows = items
        .map((item, index) => `<Relationship Id="rId${String(index + 1)}" Type="${item.type}" Target="${item.target}"/>`)
        .join('');
    return `${DECLARATION}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${rows}</Relationships>`;
}
/** The slides a map becomes: an opener, then its branches, each split at `BULLETS_PER_SLIDE`. */
export function deckSlides(document) {
    const slides = [];
    let branch = null;
    let part = 0;
    let bullets = [];
    // A new slide for the branch in hand: the first under its own name, the rest numbered.
    const open = (title) => {
        part += 1;
        bullets = [];
        slides.push({ title: part === 1 ? title : `${title} (${String(part)})`, bullets });
    };
    for (const item of outlineOf(document)) {
        if (item.depth === 0)
            continue;
        if (item.depth === 1) {
            branch = item.text;
            part = 0;
            open(branch);
            continue;
        }
        if (branch === null)
            continue;
        if (bullets.length === BULLETS_PER_SLIDE)
            open(branch);
        bullets.push(item);
    }
    return slides;
}
export function renderPptx(document, options = {}) {
    const title = document.map.title.trim() === '' ? 'Untitled map' : document.map.title.trim();
    const branches = deckSlides(document);
    const ideas = Math.max(0, outlineOf(document).length - 1);
    const subtitle = `${String(ideas)} ${ideas === 1 ? 'idea' : 'ideas'} · ${String(branches.length)} ${branches.length === 1 ? 'branch' : 'branches'}`;
    const slides = [
        openingSlide(title, subtitle, options.picture),
        ...branches.map((entry) => branchSlide(entry.title, entry.bullets, 1)),
    ];
    const layoutRel = { type: `${REL}/slideLayout`, target: '../slideLayouts/slideLayout1.xml' };
    const entries = [
        {
            path: '[Content_Types].xml',
            data: `${DECLARATION}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
                '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
                '<Default Extension="xml" ContentType="application/xml"/>' +
                '<Default Extension="png" ContentType="image/png"/>' +
                '<Override PartName="/ppt/presentation.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.presentation.main+xml"/>' +
                '<Override PartName="/ppt/slideMasters/slideMaster1.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.slideMaster+xml"/>' +
                '<Override PartName="/ppt/slideLayouts/slideLayout1.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.slideLayout+xml"/>' +
                '<Override PartName="/ppt/theme/theme1.xml" ContentType="application/vnd.openxmlformats-officedocument.theme+xml"/>' +
                slides
                    .map((_, index) => `<Override PartName="/ppt/slides/slide${String(index + 1)}.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.slide+xml"/>`)
                    .join('') +
                '<Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>' +
                '<Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>' +
                '</Types>',
        },
        {
            path: '_rels/.rels',
            data: relationships([
                { type: `${REL}/officeDocument`, target: 'ppt/presentation.xml' },
                {
                    type: 'http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties',
                    target: 'docProps/core.xml',
                },
                { type: `${REL}/extended-properties`, target: 'docProps/app.xml' },
            ]),
        },
        {
            path: 'docProps/core.xml',
            data: `${DECLARATION}<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" ` +
                'xmlns:dc="http://purl.org/dc/elements/1.1/"><dc:title>' +
                `${xml(title)}</dc:title><dc:creator>Orbit Mind</dc:creator></cp:coreProperties>`,
        },
        {
            path: 'docProps/app.xml',
            data: `${DECLARATION}<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties">` +
                `<Application>Orbit Mind</Application><Slides>${String(slides.length)}</Slides></Properties>`,
        },
        {
            path: 'ppt/presentation.xml',
            data: `${DECLARATION}<p:presentation ${NS} saveSubsetFonts="1">` +
                '<p:sldMasterIdLst><p:sldMasterId id="2147483648" r:id="rId1"/></p:sldMasterIdLst><p:sldIdLst>' +
                slides
                    .map((_, index) => `<p:sldId id="${String(256 + index)}" r:id="rId${String(index + 2)}"/>`)
                    .join('') +
                `</p:sldIdLst><p:sldSz cx="${String(SLIDE_WIDTH)}" cy="${String(SLIDE_HEIGHT)}"/>` +
                '<p:notesSz cx="6858000" cy="9144000"/></p:presentation>',
        },
        {
            path: 'ppt/_rels/presentation.xml.rels',
            data: relationships([
                { type: `${REL}/slideMaster`, target: 'slideMasters/slideMaster1.xml' },
                ...slides.map((_, index) => ({
                    type: `${REL}/slide`,
                    target: `slides/slide${String(index + 1)}.xml`,
                })),
                { type: `${REL}/theme`, target: 'theme/theme1.xml' },
            ]),
        },
        { path: 'ppt/theme/theme1.xml', data: THEME },
        { path: 'ppt/slideMasters/slideMaster1.xml', data: MASTER },
        {
            path: 'ppt/slideMasters/_rels/slideMaster1.xml.rels',
            data: relationships([layoutRel, { type: `${REL}/theme`, target: '../theme/theme1.xml' }]),
        },
        { path: 'ppt/slideLayouts/slideLayout1.xml', data: LAYOUT },
        {
            path: 'ppt/slideLayouts/_rels/slideLayout1.xml.rels',
            data: relationships([
                { type: `${REL}/slideMaster`, target: '../slideMasters/slideMaster1.xml' },
            ]),
        },
        ...slides.flatMap((content, index) => [
            { path: `ppt/slides/slide${String(index + 1)}.xml`, data: content },
            {
                path: `ppt/slides/_rels/slide${String(index + 1)}.xml.rels`,
                data: relationships(index === 0 && options.picture !== undefined
                    ? [layoutRel, { type: `${REL}/image`, target: '../media/image1.png' }]
                    : [layoutRel]),
            },
        ]),
    ];
    if (options.picture !== undefined) {
        entries.push({ path: 'ppt/media/image1.png', data: options.picture.bytes });
    }
    return zip(entries);
}
//# sourceMappingURL=pptx.js.map